import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Save, ArrowLeft } from "lucide-react";

import DashboardLayout from "@/components/DashboardLayout";
import { api, extractErrorMessage } from "@/lib/api";
import {
  FORMATS, GENRES, STAGES, COUNTRIES, VALUES_LIST, INTERESTS,
  OBJECTIVES, KPIS, CONTRIBUTION_TYPES,
} from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";

const empty = {
  title: "",
  description: "",
  objective: "",
  brand_id: "",
  audience_age_min: null,
  audience_age_max: null,
  audience_countries: [],
  audience_interests: [],
  audience_platforms: [],
  objectives: [],
  kpis: [],
  contribution_types: [],
  placement_types: [],
  budget_min: null,
  budget_max: null,
  currency: "USD",
  accepts_products: false,
  accepts_services: false,
  preferred_formats: [],
  preferred_genres: [],
  preferred_stages: [],
  territories: [],
  values: [],
  hidden_identity: false,
  active: true,
};

export default function BriefEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [brief, setBrief] = useState(empty);
  const [brands, setBrands] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/api/brands");
      setBrands(data);
      if (id) {
        const { data: b } = await api.get(`/api/briefs/${id}`);
        setBrief({ ...empty, ...b });
      }
    })();
  }, [id]);

  const set = (patch) => setBrief((v) => ({ ...v, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      if (!brief.title.trim()) return toast.error(t("editor_brief.title_required"));
      if (id) await api.put(`/api/briefs/${id}`, brief);
      else await api.post("/api/briefs", brief);
      toast.success(t("editor_brief.toast_saved"));
      nav("/dashboard/briefs");
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout
      title={id ? t("editor_brief.title_edit") : t("editor_brief.title_new")}
      subtitle={t("editor_brief.subtitle")}
      actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/dashboard/briefs")} className="rounded-full">
            <ArrowLeft size={16} className="mr-2" /> {t("editor_brief.back")}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="button-save-brief" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
            <Save size={16} className="mr-2" /> {saving ? t("editor_brief.saving") : t("editor_brief.save")}
          </Button>
        </div>
      }
    >
      <Tabs defaultValue="basic">
        <TabsList className="mb-6 bg-slate-100 rounded-full">
          <TabsTrigger value="basic">{t("editor_brief.tab_basic")}</TabsTrigger>
          <TabsTrigger value="audience">{t("editor_brief.tab_audience")}</TabsTrigger>
          <TabsTrigger value="contribution">{t("editor_brief.tab_contribution")}</TabsTrigger>
          <TabsTrigger value="preferences">{t("editor_brief.tab_preferences")}</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <Card>
            <div className="grid md:grid-cols-2 gap-5">
              <Field label={t("editor_brief.field_title")} required>
                <Input value={brief.title} onChange={(e) => set({ title: e.target.value })} data-testid="input-brief-title" />
              </Field>
              <Field label={t("editor_brief.field_brand")}>
                <Select value={brief.brand_id || "none"} onValueChange={(v) => set({ brand_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder={t("editor_brief.pick_brand")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t("editor_brief.no_brand")}</SelectItem>
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <Field label={t("editor_brief.field_objective")} className="mt-5">
              <Input value={brief.objective || ""} onChange={(e) => set({ objective: e.target.value })} placeholder={t("editor_brief.objective_placeholder")} />
            </Field>
            <Field label={t("editor_brief.field_description")} className="mt-5">
              <Textarea rows={4} value={brief.description || ""} onChange={(e) => set({ description: e.target.value })} />
            </Field>
            <div className="mt-5 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">{t("editor_brief.hide_identity")}</p>
                <p className="text-xs text-slate-500">{t("editor_brief.hide_identity_hint")}</p>
              </div>
              <Switch checked={!!brief.hidden_identity} onCheckedChange={(v) => set({ hidden_identity: v })} />
            </div>
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">{t("editor_brief.active")}</p>
                <p className="text-xs text-slate-500">{t("editor_brief.active_hint")}</p>
              </div>
              <Switch checked={!!brief.active} onCheckedChange={(v) => set({ active: v })} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="audience" className="space-y-6">
          <Card>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("editor_brief.field_age_min")}><Input type="number" value={brief.audience_age_min ?? ""} onChange={(e) => set({ audience_age_min: e.target.value ? parseInt(e.target.value, 10) : null })} /></Field>
                <Field label={t("editor_brief.field_age_max")}><Input type="number" value={brief.audience_age_max ?? ""} onChange={(e) => set({ audience_age_max: e.target.value ? parseInt(e.target.value, 10) : null })} /></Field>
              </div>
            </div>
            <div className="mt-5 space-y-5">
              <ChipsField t={t} label={t("editor_brief.field_countries")} values={brief.audience_countries} onChange={(v) => set({ audience_countries: v })} suggestions={COUNTRIES} />
              <ChipsField t={t} label={t("editor_brief.field_interests")} values={brief.audience_interests} onChange={(v) => set({ audience_interests: v })} suggestions={INTERESTS} />
              <ChipsField t={t} label={t("editor_brief.field_platforms")} values={brief.audience_platforms} onChange={(v) => set({ audience_platforms: v })} suggestions={["Netflix", "TikTok", "YouTube", "Instagram", "Spotify", "Festival"]} />
            </div>
            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <ChipsField t={t} label={t("editor_brief.field_objectives")} values={brief.objectives} onChange={(v) => set({ objectives: v })} suggestions={OBJECTIVES} />
              <ChipsField t={t} label={t("editor_brief.field_kpis")} values={brief.kpis} onChange={(v) => set({ kpis: v })} suggestions={KPIS} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="contribution" className="space-y-6">
          <Card>
            <h3 className="section-title">{t("editor_brief.section_collab")}</h3>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              {CONTRIBUTION_TYPES.map((c) => (
                <label key={c.value} className="flex items-center gap-3 text-sm text-slate-700 border border-slate-200 rounded-lg p-3 cursor-pointer hover:border-slate-300">
                  <Checkbox
                    checked={(brief.contribution_types || []).includes(c.value)}
                    onCheckedChange={(chk) => {
                      const list = new Set(brief.contribution_types || []);
                      if (chk) list.add(c.value); else list.delete(c.value);
                      set({ contribution_types: [...list] });
                    }}
                    data-testid={`checkbox-contribution-${c.value}`}
                  />
                  {c.label}
                </label>
              ))}
            </div>
            <div className="mt-6 grid md:grid-cols-3 gap-5">
              <Field label={t("editor_brief.field_budget_min")}><Input type="number" value={brief.budget_min ?? ""} onChange={(e) => set({ budget_min: e.target.value ? parseFloat(e.target.value) : null })} /></Field>
              <Field label={t("editor_brief.field_budget_max")}><Input type="number" value={brief.budget_max ?? ""} onChange={(e) => set({ budget_max: e.target.value ? parseFloat(e.target.value) : null })} /></Field>
              <Field label={t("editor_brief.field_currency")}>
                <Select value={brief.currency} onValueChange={(v) => set({ currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["USD", "EUR", "CLP", "MXN", "ARS", "COP", "PEN", "BRL"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="mt-6 flex flex-col gap-3">
              <SwitchRow label={t("editor_brief.accept_products")} value={brief.accepts_products} onChange={(v) => set({ accepts_products: v })} />
              <SwitchRow label={t("editor_brief.accept_services")} value={brief.accepts_services} onChange={(v) => set({ accepts_services: v })} />
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="preferences" className="space-y-6">
          <Card>
            <div className="space-y-5">
              <ChipsField t={t} label={t("editor_brief.field_formats")} values={brief.preferred_formats} onChange={(v) => set({ preferred_formats: v })} suggestions={FORMATS.map((f) => f.value)} />
              <ChipsField t={t} label={t("editor_brief.field_genres")} values={brief.preferred_genres} onChange={(v) => set({ preferred_genres: v })} suggestions={GENRES} />
              <ChipsField t={t} label={t("editor_brief.field_stages")} values={brief.preferred_stages} onChange={(v) => set({ preferred_stages: v })} suggestions={STAGES.map((s) => s.value)} />
              <ChipsField t={t} label={t("editor_brief.field_territories")} values={brief.territories} onChange={(v) => set({ territories: v })} suggestions={COUNTRIES} />
              <ChipsField t={t} label={t("editor_brief.field_values")} values={brief.values} onChange={(v) => set({ values: v })} suggestions={VALUES_LIST} />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function Card({ children }) { return <div className="kpi-card p-6">{children}</div>; }
function Field({ label, children, required, className }) {
  return (
    <div className={className}>
      <Label className="text-sm text-slate-600">{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
function SwitchRow({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={!!value} onCheckedChange={onChange} />
    </div>
  );
}
function ChipsField({ t, label, values, onChange, suggestions }) {
  const [input, setInput] = useState("");
  const add = (v) => {
    const clean = (v || "").trim();
    if (!clean) return;
    if ((values || []).includes(clean)) return;
    onChange([...(values || []), clean]);
    setInput("");
  };
  return (
    <div>
      <Label className="text-sm text-slate-600">{label}</Label>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {(values || []).map((v) => (
          <span key={v} className="text-sm inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-3 py-1">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="text-slate-500 hover:text-red-600">×</button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add(input))} placeholder={t("editor_brief.chips_add_placeholder")} />
        <Button type="button" variant="outline" onClick={() => add(input)}>{t("editor_brief.chips_add")}</Button>
      </div>
      {suggestions?.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.filter((s) => !(values || []).includes(s)).slice(0, 10).map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className="text-xs text-slate-500 hover:text-petroleum border border-slate-200 rounded-full px-2 py-0.5">+ {s}</button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
